import { checkClaimsWeb, IRocketTokens, spErrorTags } from "@eventr/common";
import type { ChatCacheAdapter, ChatConfig, ChatError, ChatUser } from "@eventr/common-storybook";
import type { AppUser } from "@eventr/common";
import { defaultAddSnack } from "state/overlayState/overlayContext";
import { getRocketChatUrl, getRocketChatWebsocket } from "utils";
import { gatewayConfig } from "../gatewayClient/config";
import { chatBackend, spchatUrl } from "./chatBackend";
import { getChatCache, getChatStore } from "./chatStore";
import { ROCKET_ENDPOINTS } from "./endpoints";
import { cacheChatMedia } from "./chatMediaCache";
import { appUserFullName } from "./hooks/useGetChatName";

/**
 * A loadable URL for a chat upload. `<img>` and `<video>` cannot send the chat credential
 * as a header, so files go through a proxy that takes it in the query string: the
 * gateway's for Rocket.Chat, spchat's own for spchat sessions.
 */
export const chatFileProxyUrl = (fileUrl: string, userId: string, authToken: string): string => {
  const base =
    chatBackend() === "spchat"
      ? `${spchatUrl()}/api/chat/file`
      : `${gatewayConfig().baseUrl}${ROCKET_ENDPOINTS.downloadFile}`;
  const query = new URLSearchParams({ userId, token: authToken, fileUrl });
  return `${base}?${query.toString()}`;
};

/** Room history in the app's existing `chatStore` IndexedDB, so a room opens on its last messages. */
const messageCache: ChatCacheAdapter = {
  get: async <T>(key: string) => (await getChatCache<T>("messages", key)) ?? undefined,
  set: async (key, value) => {
    await getChatStore()?.set("messages", key, value);
  },
  clear: async () => undefined,
};

/** User-facing failures get a snack; everything is logged. */
const reportChatError = (error: ChatError) => {
  spErrorTags(error.message, ["rocketchat"], error.cause);
  if (error.scope === "send" || error.scope === "upload" || error.scope === "action") {
    defaultAddSnack(error.message, "error");
  }
};

export interface AppChatPresentation {
  locale: string;
  timeFormat: string;
  /** The app's users, which name the people in a chat. */
  users: AppUser[];
}

/**
 * A chat user's name as the app shows it, from the app user their chat account was made for
 * (`customFields.wpUserID`, set by wp-rocketchat-sync). `undefined` keeps the chat's own name.
 */
export const appChatDisplayName =
  (users: AppUser[]) =>
  (user: ChatUser): string | undefined => {
    const wpUserId = Number(user.customFields?.wpUserID);
    if (!wpUserId) return undefined;
    const appUser = users.find((u) => u.id === wpUserId);
    return (appUser && appUserFullName(appUser)) || undefined;
  };

/** The chat component library's config for the signed-in user's chat on one organisation. */
export const appChatConfig = (token: IRocketTokens, presentation: AppChatPresentation): ChatConfig => ({
  host: getRocketChatUrl(token.org),
  websocketUrl: getRocketChatWebsocket(token.org),
  org: token.org,
  userId: token.userId,
  authToken: token.authToken,
  resolveFileUrl: (absoluteUrl) => chatFileProxyUrl(absoluteUrl, token.userId, token.authToken),
  locale: presentation.locale,
  timeFormat: presentation.timeFormat,
  permissions: { canSend: checkClaimsWeb(["/"], ["sendMessage"]) },
  cache: messageCache,
  mediaCache: cacheChatMedia,
  displayName: appChatDisplayName(presentation.users),
  onError: reportChatError,
});
